"use client";
import React, { useRef, useEffect, useState } from "react";
import { useScroll, useTransform, motion } from "framer-motion";
import AOS from "aos";
import "aos/dist/aos.css";
import "../styles/Elecinstallation.css";

import Beams from "./Beams";

// ✅ Imports
import image1 from "../assets/cj6.jfif";
import image2 from "../assets/cj2.avif";
import image3 from "../assets/cj3.webp";
import image4 from "../assets/cj4.webp";
import image5 from "../assets/cj5.webp";
import image6 from "../assets/cj8.jpg";
import image7 from "../assets/cj9.jfif";
import image8 from "../assets/cj10.png";
import image9 from "../assets/cj11.jpg";
import image10 from "../assets/cj1.jpg";
import image11 from "../assets/cj13.jpg";
import image12 from "../assets/cj14.jpeg";
import image13 from "../assets/cj12.jpg";

const CableHeroSection = ({ data }) => {
  const timelineRef = useRef(null);
  const containerRef = useRef(null);
  const [timelineHeight, setTimelineHeight] = useState(0);

  useEffect(() => {
    AOS.init({ duration: 800, once: true });
  }, []);

  useEffect(() => {
    const calculateHeight = () => {
      if (timelineRef.current) {
        // Use a small timeout to ensure the DOM has painted completely
        setTimeout(() => {
          setTimelineHeight(timelineRef.current.getBoundingClientRect().height);
        }, 500);
      }
    };
    
    calculateHeight();
    window.addEventListener("resize", calculateHeight);
    // Also recalculate when window is fully loaded (images ready)
    window.addEventListener("load", calculateHeight);
    
    return () => {
      window.removeEventListener("resize", calculateHeight);
      window.removeEventListener("load", calculateHeight);
    };
  }, [data]);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start 10%", "end 50%"],
  });

  const heightTransform = useTransform(scrollYProgress, [0, 1], [0, timelineHeight]);
  const opacityTransform = useTransform(scrollYProgress, [0, 0.1], [0, 1]);

  return (
    <div className="elec-design-hero-container" ref={containerRef}>
      <div className="elec-design-hero-header">
        <h2 className="elec-design-hero-title">Expert Jointing</h2>
        <p className="elec-design-hero-description">
          Delivering precision-engineered cable management and termination services tailored for high-demand industrial complexes and national infrastructure projects.
        </p>
      </div>

      <div ref={timelineRef} className="elec-design-hero-timeline">
        {data.map((item, index) => (
          <div key={index} className="elec-design-hero-step">
            <div className="elec-design-hero-sticky">
              <div className="elec-design-hero-marker-outer"><div className="elec-design-hero-marker-inner" /></div>
              <h3 className="elec-design-hero-step-title-desktop">{item.title}</h3>
            </div>
            <div className="elec-design-hero-step-content">
              <h3 className="elec-design-hero-step-title-mobile">{item.title}</h3>
              <img src={item.image} alt={item.title} className="elec-design-hero-step-image" data-aos="fade-left" />
              <div className="elec-design-hero-step-paragraph" data-aos="fade-left">
                <p>{item.text}</p>
                {item.points && item.points.map((point, idx) => (
                  <p key={idx} className="elec-design-hero-step-point">
                    <span className="elec-design-hero-arrow-icon">→</span> {point}
                  </p>
                ))}
              </div>
            </div>
          </div>
        ))}
        <div style={{ height: `${timelineHeight}px` }} className="elec-design-hero-progress-bar-wrapper">
          <motion.div style={{ height: heightTransform, opacity: opacityTransform }} className="elec-design-hero-progress-bar" />
        </div>
      </div>
    </div>
  );
};

const Cablepg = () => {
  // ✅ FIX: Automatic Reload Logic for Timeline Bug
  useEffect(() => {
    const hasReloaded = sessionStorage.getItem("cablePageReloaded");
    if (!hasReloaded) {
      sessionStorage.setItem("cablePageReloaded", "true");
      window.location.reload();
    } else {
      // If already reloaded, scroll to top and clear the session storage on page unmount if needed, 
      // or just keep it to prevent infinite loops.
      window.scrollTo(0, 0);
    }
  }, []);

  const timelineData = [
    {
      title: "Cable Laying Guidelines",
      image: image10,
      text: "Proper installation requires meticulous mechanical handling and environmental protection to prevent sheath damage and ensure long-term performance.",
      points: [
          "Route & Trenching",
          "Bending Radius",
          "Spacing & Formation",
          "Protection"
      ],
    },
    {
      title: "Cable Termination",
      image: image11,
      text: "As most HV failures occur at connections, we prioritize high-precision workmanship to ensure termination and jointing reliability.",
      points: [
          "Stress Relief: Precision installation of stress cones to redistribute electric fields at screen cut-backs and prevent flashover.",
          "Anti-Tracking: Utilization of high-creepage sheds and skirts to prevent surface leakage current in outdoor and humid environments.",
          "Crimping & Shear Bolts: Application of engineered lugs and torque-controlled bolts for low-resistance, high-strength electrical bonds.",
          "Earthing: Secure grounding of cable armoring and metallic shields to provide a safe, high-capacity fault return path."
      ],
    },
    {
      title: "Fault Analysis & Diagnosis",
      image: image12,
      text: "Our diagnostic testing protocols assess the overall health of the dielectric system, identifying hidden weaknesses before they lead to outages.",
      points: [
          "Insulation Resistance (Megger): Initial screening to measure dielectric health and detect immediate leakage or ground faults.",
          "VLF Withstand Testing: Applying 0.1Hz AC voltage to fail existing weak spots safely without damaging healthy insulation.",
          "Tan Delta Analysis: Measuring dielectric loss (dissipation factor) to assess insulation aging and moisture ingress levels.",
          "Partial Discharge (PD): High-sensitivity diagnostics to identify localized insulation voids and predict imminent component failure."
      ],
    },
    {
      title: "Fault Location Methodology",
      image: image13,
      text: "When faults occur, we utilize a two-step process to minimize downtime: rapid pre-location followed by high-accuracy pinpointing.",
      points: [
         "Continuity Testing: Measures low resistance to ensure an uninterrupted path for current flow, detecting breaks or high-resistance connections.",
          "Pre-location (TDR): Utilizing Time Domain Reflectometry to measure signal reflections and estimate distance to the fault.",
          "ARM / ICM: Leveraging Arc Reflection and Impulse Current methods to convert high-resistance faults into measurable transients.",
          "Surge Wave Generation (Thumper): Injecting high-energy DC pulses to create a discharge arc at the exact fault location.",
          "Acoustic & Magnetic Detection: Using ground microphones and sensors to trace the 'thump' and confirm the physical failure point."
      ],
    },
   {
      title: "Operational Excellence",
      image: image7,
      text: "We provide high-reliability cable management solutions, ensuring seamless power distribution across massive industrial footprints.",
      points: [
          "Manned by dedicated technicians overseeing end-to-end execution.",
          "Rigorous schedule preparation and vigilant project monitoring.",
          "Integrated troubleshooting and maintenance for long-term network stability."
      ],
    },


  ];

  return (
    <>
      <div className="elec-installation-page-container">
        <div className="elec-installation-page-header">
          <h1>Cable Laying and Termination Works</h1>
          <p>
            Specialized providers of highly affordable and reliable cable laying, termination, and glanding services. We bridge the gap between complex electrical design and field-tested implementation.
          </p>
          <div className="cable-specifications-container">
            <div className="cable-spec-grid">
              <div className="spec-card" data-aos="zoom-in">
                <div className="spec-icon">⚡</div>
                <h4>Ultra-High Voltage</h4>
                <p>Cable laying upto <strong>400kv</strong></p>
              </div>
              <div className="spec-card" data-aos="zoom-in" data-aos-delay="100">
                <div className="spec-icon">🔌</div>
                <h4>Expert Jointing</h4>
                <p>Cable jointing and termination upto <strong>33kv</strong></p>
              </div>
               <div className="spec-card" data-aos="zoom-in" data-aos-delay="100">
                <div className="spec-icon">🔍</div>
            <h4>Fault Diagnosis</h4>
            <p>Identifying hidden dielectric weaknesses to prevent costly system outages.</p>
          </div>
              <div className="spec-card" data-aos="zoom-in" data-aos-delay="200">
                <div className="spec-icon">📜</div>
                <h4>Quality Assurance</h4>
                <p><strong>DEWA approved jointers</strong></p>
              </div>
            </div>
          </div>
        </div>

        <div className="elec-installation-cube-wrapper">
          <div className="elec-installation-cube-gallery">
            <img src={image1} alt="Cable 1" /><img src={image2} alt="Cable 2" /><img src={image3} alt="Cable 3" />
            <img src={image4} alt="Cable 4" /><img src={image5} alt="Cable 5" /><img src={image6} alt="Cable 6" />
          </div>
        </div>

        <div className="elec-installation-silk-bg">
          <Beams beamWidth={1.4} beamHeight={30} beamNumber={14} lightColor="#00CCFF" speed={20} noiseIntensity={0} scale={0.19} rotation={59} />
        </div>
      </div>

      <CableHeroSection data={timelineData} />
    </>
  );
};

export default Cablepg;